/**
 * MFA Modal Component
 * 
 * Modal for collecting MFA/auth codes during extraction.
 * Matches the theme and style of PlatformCredentialsModal.
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, ShieldCheckIcon, ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { InteractionRequest } from '../../types/userInteraction';

interface MFAModalProps {
  isOpen: boolean;
  request: InteractionRequest | null;
  onSubmit: (code: string) => void;
  onCancel: () => void;
}

export const MFAModal: React.FC<MFAModalProps> = ({
  isOpen,
  request,
  onSubmit,
  onCancel
}) => {
  const [code, setCode] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isExpired, setIsExpired] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Countdown timer
  useEffect(() => {
    if (!isOpen || !request) return;

    const calculateRemaining = () => {
      const expiryTime = new Date(request.expires_at).getTime();
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((expiryTime - now) / 1000));
      
      setTimeRemaining(remaining);
      setIsExpired(remaining === 0);
    };

    calculateRemaining();
    const interval = setInterval(calculateRemaining, 1000);

    return () => clearInterval(interval);
  }, [isOpen, request]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setCode('');
      setIsExpired(false);
      setIsSubmitting(false);
      
      // Focus input
      setTimeout(() => {
        document.getElementById('mfa-code-input')?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Handle body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) return; // Prevent multiple submissions
    
    const inputFormat = request?.input_format || { type: 'numeric', length: 6 };
    const requiredLength = inputFormat.length || 6;
    
    if (code.trim().length === requiredLength) {
      setIsSubmitting(true);
      try {
        await onSubmit(code);
        setCode('');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen || !request) return null;

  return createPortal(
    <div 
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onCancel}
      onKeyDown={handleKeyDown}
    >
      <div 
        className="relative bg-theme-bg dark:bg-dark-theme-bg rounded-lg shadow-2xl w-full max-w-md mx-4 overflow-hidden border border-theme-border dark:border-dark-theme-border"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-theme-border dark:border-dark-theme-border">
          <div className="flex items-center space-x-3">
            <ShieldCheckIcon className="w-6 h-6 text-theme-text-secondary dark:text-dark-theme-text-secondary" />
            <h3 className="text-xl font-semibold text-theme-text dark:text-dark-theme-text">
              Authentication Required
            </h3>
          </div>
          <button
            onClick={onCancel}
            className="text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text transition-colors"
            aria-label="Close"
            title="Close authentication modal"
          >
            <XMarkIcon className="w-6 h-6" aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              {request.prompt_message}
            </p>
          </div>

          {/* Code Input */}
          <div className="mb-6">
            <label 
              htmlFor="mfa-code-input"
              className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary mb-2"
            >
              {request.input_format?.label || 'Authentication Code'}
            </label>
            <input
              type="text"
              id="mfa-code-input"
              value={code}
              onChange={(e) => {
                const inputFormat = request.input_format || { type: 'numeric', length: 6 };
                let value = e.target.value;
                
                // Apply format-specific filtering
                if (inputFormat.type === 'numeric') {
                  value = value.replace(/\D/g, '');
                } else if (inputFormat.type === 'alphanumeric') {
                  value = value.replace(/[^A-Za-z0-9]/g, '');
                }
                
                // Apply length limit
                if (inputFormat.length) {
                  value = value.slice(0, inputFormat.length);
                }
                
                setCode(value);
              }}
              placeholder={request.input_format?.placeholder || '000000'}
              maxLength={request.input_format?.length || 6}
              pattern={request.input_format?.pattern || '[0-9]{6}'}
              required
              disabled={isExpired}
              className="w-full px-4 py-3 rounded-md border border-theme-border dark:border-dark-theme-border bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text text-center text-2xl tracking-widest focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              autoComplete="off"
            />
          </div>

          {/* Timer */}
          <div className="mb-6 p-4 rounded-md border bg-theme-bg dark:bg-dark-theme-bg border-theme-border dark:border-dark-theme-border">
            <div className="flex items-center justify-center space-x-2">
              {isExpired ? (
                <>
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
                  <p className="text-sm text-red-500">
                    Request expired. Please start a new job group.
                  </p>
                </>
              ) : (
                <>
                  <ClockIcon className={`w-5 h-5 ${timeRemaining < 60 ? 'text-red-500' : 'text-yellow-600 dark:text-yellow-500'}`} />
                  <p className={`text-sm font-medium ${timeRemaining < 60 ? 'text-red-500' : 'text-yellow-700 dark:text-yellow-400'}`}>
                    Time remaining: {formatTime(timeRemaining)}
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 font-medium text-sm rounded-md bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary text-theme-text dark:text-dark-theme-text hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isExpired || isSubmitting || code.length !== (request?.input_format?.length || 6)}
              className="flex items-center px-6 py-2 space-x-2 text-white rounded-md transition-opacity duration-150 bg-primary-light dark:bg-primary-dark hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light dark:focus:ring-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ShieldCheckIcon className="w-4 h-4" />
              <span>{isSubmitting ? 'Verifying...' : 'Verify Code'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};
